import { Currency } from "../checkout/types/checkoutTypes"
import { CartProductWithQuantity } from "../ts/interfaces"
import { Countries } from "./Countries"
import { getCookie } from "./klaviyo"
import { getFbc } from "./queryParams"

declare global {
  interface Window {
    gaGlobal: { vid: string } | undefined
  }
}

let queuedGEvents: { eventName: string; data: any; email?: string }[] = []
let queuedPEvents: { eventType: string; fbqObj: any; eventID: string }[] = []

let haveSetGUserData = false
let haveSetFUserData = false
let phoneFormatted = ""

const generateNewID = (length: number): string => {
  if (length < 1) {
    throw new RangeError("Length must be at least 1")
  }
  const possible = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ"
  let string = ""
  for (let i = 0; i < length; i += 1) {
    string += possible.charAt(Math.floor(Math.random() * possible.length))
  }

  return string
}

export type PixelEventType =
  | "Lead"
  | "AddToCart"
  | "Purchase"
  | "InitiateCheckout"
  | "ViewContent"
  | "add_shipping_info"
  | "view_cart"
  | "LpClickThrough"
  | "Engaged_40_40"

type EventCartData = {
  items: CartProductWithQuantity[]
  total: number
  currency: Currency
  confirmationCode?: string
}

const buildPixelEventDataPurchaseIntiiate = (data: EventCartData) => {
  const fbqObj = {
    content_type: "product",
    num_items: data.items.length,
    content_name: data.items[0].name,
    content_ids: [...data.items.map(product => product?.id)],
    contents: [
      ...data.items.map(product => {
        const price =
          (product && product.prices && product.prices[data.currency]) || null
        return {
          id: product?.id,
          name: product?.name,
          quantity: product?.quantity,
          price: price ? parseFloat((price.unit_amount / 100.0).toFixed(2)) : 0,
        }
      }),
    ],
    value: parseFloat((data.total / 100.0).toFixed(2)),
    currency: data.currency,
  }
  return fbqObj
}

const buildPixelEventDataAddToCartViewContent = (data: EventCartData) => {
  const cartProduct = data.items[0]

  const price =
    (cartProduct && cartProduct.prices && cartProduct.prices[data.currency]) ||
    null

  const fbqObj = {
    content_type: "product",
    content_ids: [cartProduct.id],
    content_name: cartProduct.name,
    content_category: "headset",
    contents: [
      {
        id: cartProduct?.id,
        name: cartProduct?.name,
        quantity: cartProduct?.quantity,
      },
    ],
    ...(price && {
      value: parseFloat((price.unit_amount / 100.0).toFixed(2)),
    }),
    currency: data.currency,
  }
  return fbqObj
}

const buildGtagEventDataAddToCartViewContent = (data: EventCartData) => {
  const cartProduct = data.items[0]

  const price =
    (cartProduct && cartProduct.prices && cartProduct.prices[data.currency]) ||
    null

  const gObj = {
    items: [
      {
        item_id: cartProduct?.id,
        item_name: cartProduct?.name,
        quantity: cartProduct?.quantity,
        price: price ? parseFloat((price.unit_amount / 100.0).toFixed(2)) : 0,
      },
    ],
    ...(price && {
      value: parseFloat((price.unit_amount / 100.0).toFixed(2)),
    }),
    currency: data.currency.toUpperCase(),
  }
  return gObj
}

const buildGtagEventDataPurchaseInitiate = (data: EventCartData) => {
  const gObj = {
    items: [
      ...data.items.map(product => {
        const price =
          (product && product.prices && product.prices[data.currency]) || null
        if (!price) {
          return null
        }
        return {
          item_id: product?.id,
          item_name: product?.name,
          quantity: product?.quantity,
          price: parseFloat((price.unit_amount / 100.0).toFixed(2)),
        }
      }),
    ],
    ...(data.confirmationCode && { transaction_id: data.confirmationCode }),
    value: parseFloat((data.total / 100.0).toFixed(2)),
    currency: data.currency.toUpperCase(),
  }
  return gObj
}

const buildPixelEventData = (
  eventType: PixelEventType,
  data?: EventCartData
) => {
  if (eventType === "Lead") {
    return {
      content_category: "email",
      content_name: "none",
    }
  }
  if (eventType === "LpClickThrough" || eventType === "Engaged_40_40") {
    return {}
  }
  if (!data || data.items.length <= 0) {
    console.log("No items provided to pixel")
    return null
  }

  if (eventType === "AddToCart" || eventType === "ViewContent") {
    return buildPixelEventDataAddToCartViewContent(data)
  }
  return buildPixelEventDataPurchaseIntiiate(data)
}

const eventMap = {
  Lead: "generate_lead",
  AddToCart: "add_to_cart",
  Purchase: "purchase",
  InitiateCheckout: "begin_checkout",
  ViewContent: "view_item",
  add_shipping_info: "add_shipping_info",
  view_cart: "view_cart",
  LpClickThrough: "LpClickThrough",
}

const sendGtagEventData = async (
  eventType: PixelEventType,
  id: string,
  data?: EventCartData,
  email?: string
) => {
  if (
    eventType === "Lead" ||
    eventType === "LpClickThrough" ||
    eventType === "Engaged_40_40"
  ) {
    await queueOrSendGoogleEvent(eventMap[eventType], undefined, email)
    return
  }
  if (!data || data.items.length <= 0) {
    console.log("No items provided to pixel")
    return
  }

  const eventData =
    eventType === "AddToCart" || eventType === "ViewContent"
      ? buildGtagEventDataAddToCartViewContent(data)
      : buildGtagEventDataPurchaseInitiate(data)

  await queueOrSendGoogleEvent(
    eventMap[eventType],
    {
      ...eventData,
      transaction_id: id,
    },
    email
  )
}

export const sendQueuedEvents = async () => {
  const tempGEvents = [...queuedGEvents]
  queuedGEvents = []
  await Promise.all(
    tempGEvents.map(e => queueOrSendGoogleEvent(e.eventName, e.data, e.email))
  )

  const tempPEvents = [...queuedPEvents]
  queuedPEvents = []
  await Promise.all(
    tempPEvents.map(e =>
      queueOrSendPixelEvent(e.eventType, e.fbqObj, e.eventID)
    )
  )
}

export const queueOrSendGoogleEvent = async (
  eventName: string,
  data?: any,
  email?: string
) => {
  if (typeof window === `undefined` || !window.trackingInit || !window.gtag) {
    //queue the event
    queuedGEvents.push({ eventName, data, email })
    return
  }
  if (!haveSetGUserData && email) {
    haveSetGUserData = true
    const encodedE = await cleanEncodeUserDataG(email)
    window.gtag("set", "user_data", {
      ...(phoneFormatted
        ? { sha256_phone_number: await digestMessage("+" + phoneFormatted) }
        : {}),
      sha256_email_address: encodedE,
    })
    window.gtag("set", "user_id", encodedE)
  }
  if (data) {
    window.gtag("event", eventName, data)
  } else {
    window.gtag("event", eventName)
  }
}

export const queueOrSendPixelEvent = async (
  eventType: string,
  fbqObj: any,
  eventID: string,
  userData?: {
    email?: string
    firstName?: string
    lastName?: string
    phone?: string
    country?: string
  }
) => {
  if (typeof window === `undefined` || !window.trackingInit || !window.fbq) {
    //queue the event
    queuedPEvents.push({ eventType, fbqObj, eventID })
    return
  }
  if (!haveSetFUserData && userData?.email && window?.pixelId) {
    haveSetFUserData = true
    const encodedE = await cleanEncodeUserDataG(userData.email)
    window.fbq("init", window.pixelId, {
      em: encodedE,
      ...(userData.firstName
        ? { fn: userData.firstName.trim().toLowerCase() }
        : {}),
      ...(userData.lastName
        ? { ln: userData.lastName.trim().toLowerCase() }
        : {}),
      ...(phoneFormatted ? { ph: phoneFormatted } : {}),
    })
  }

  window.fbq("track", eventType, fbqObj, { eventID })
}

export const sendPixelEvent = async (
  eventType: PixelEventType,
  data?: EventCartData,
  userData?: {
    email?: string
    firstName?: string
    lastName?: string
    phone?: string
    country?: string
  }
) => {
  const id = data?.confirmationCode ?? `${Date.now()}-${generateNewID(8)}`
  setAndBuildPhoneFormat(userData?.country, userData?.phone)

  let fbqObj
  const promises = []
  if (eventType !== "add_shipping_info" && eventType !== "view_cart") {
    fbqObj = buildPixelEventData(eventType, data)

    if (fbqObj) {
      promises.push(queueOrSendPixelEvent(eventType, fbqObj, id, userData))
    }
  }

  promises.push(sendGtagEventData(eventType, id, data, userData?.email))
  await Promise.all(promises)

  if (eventType === "add_shipping_info" || eventType === "view_cart") {
    return
  }

  let fbp = getCookie("_fbp")
  let fbc = getCookie("_fbc")
  if (!fbc) {
    fbc = getFbc()
  }

  const user: any = {}
  if (fbc) {
    user.fbc = fbc
  }
  if (fbp) {
    user.fbp = fbp
  }
  if (userData?.email) {
    user.email = userData.email
  }
  if (userData?.firstName) {
    user.firstName = userData.firstName
  }
  if (userData?.lastName) {
    user.lastName = userData.lastName
  }
  if (phoneFormatted) {
    user.phone = phoneFormatted
  }

  await sendServerEvent(
    {
      event_id: id,
      eventType,
      user,
      client_id: getGAClientId(),
      sourceUrl: window.location.pathname,
    },
    fbqObj
  )
}

const getGAClientId = (): string => {
  if (window !== undefined && window.gaGlobal?.vid) {
    // Cookies have been accepted, get the client_id from GA4
    return window.gaGlobal.vid
  } else {
    // Cookies have not been accepted, generate a random client_id
    const getRandomStringDigits = () =>
      Math.floor(Math.random() * 1000000000).toString()

    return `${getRandomStringDigits()}.${getRandomStringDigits()}`
  }
}

const cleanEncodeUserDataG = async (email: string): Promise<string> => {
  //normailize
  const emailArr = email.trim().toLowerCase().split("@")

  const normalized =
    emailArr[1] === "gmail.com" || emailArr[1] === "googlemail.com"
      ? replaceAll(emailArr[0], ".", "").split("+")[0] + "@" + emailArr[1]
      : emailArr[0] + "@" + emailArr[1]
  return digestMessage(normalized)
}

// SHA-256 hash function
const digestMessage = async (message: string): Promise<string> => {
  const msgUint8 = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("")
  return hashHex
}

const replaceAll = function (
  str: string,
  search: string,
  replace: string
): string {
  return str.split(search).join(replace)
}

export const setAndBuildPhoneFormat = (country?: string, phone?: string) => {
  if (country && phone) {
    const countryCodeList = Countries.get(country)?.countryCallingCodes
    if (countryCodeList && countryCodeList.length > 0) {
      const countryCode = countryCodeList[0].split(" ")[0]
      const countryCodeNum = countryCode.replace("+", "")
      let ph = replaceAll(phone.trim(), " ", "")
      if (!ph.startsWith(countryCodeNum)) {
        phoneFormatted = countryCodeNum + ph
      } else {
        phoneFormatted = ph
      }
      haveSetGUserData = false // clear flag so it gets updates
      haveSetFUserData = false // clear flag so it gets updates
    }
  }
}

const sendServerEvent = async (
  event: {
    event_id: string
    eventType: PixelEventType
    client_id: string
    sourceUrl: string
    user?: {
      email?: string
      firstName?: string
      lastName?: string
      phone?: string
      country?: string
    }
  },
  fbqObj: any
) => {
  let url = process.env.GATSBY_SENSAI_SERVER_URL

  if (!url || url === "localhost" || url === "undefined") {
    url = "http://localhost:2011/local"
  }
  url = `${url}/website`
  try {
    const data = JSON.stringify({
      operationName: "sendPixelEvent",
      query: `query sendPixelEvent($pixelEvent: PixelEvent!) {
    SendEvent(input: $pixelEvent)
  }`,
      variables: {
        pixelEvent: { ...event, ...fbqObj },
      },
    })

    await fetch(url, {
      method: "post",
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
      mode: process.env.GATSBY_DEPLOY_PREVIEW ? "no-cors" : "cors",
      credentials: process.env.GATSBY_DEPLOY_PREVIEW ? "omit" : "include",
    })
  } catch (err) {
    console.log(err)
  }
}
